import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import {
  useConvitePublicoControllerAceitar,
  useConvitePublicoControllerDados,
} from '@/api/generated/ambiente-convite-público/ambiente-convite-público';
import { ApiError } from '@/api/http-client';
import { AuthShell } from '@/features/auth/components/AuthShell';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { UFS } from '@/features/auth/schemas/login.schema';
import { ROTAS } from '@/shared/lib/rotas';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { InputSenha } from '@/shared/ui/input-senha';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';

const schema = z
  .object({
    nomeCompleto: z.string().trim().min(3, 'Informe o nome completo').max(200),
    senha: z.string().min(8, 'A senha precisa de ao menos 8 caracteres'),
    confirmarSenha: z.string(),
    telefone: z.string().trim().max(20).optional().or(z.literal('')),
    crmv: z.string().trim().max(20).optional().or(z.literal('')),
    ufCrmv: z.enum(UFS).optional(),
  })
  .refine((d) => d.senha === d.confirmarSenha, {
    message: 'As senhas não conferem',
    path: ['confirmarSenha'],
  });

type Formulario = z.infer<typeof schema>;

/**
 * Aceite público de convite (módulo Administração do Ambiente).
 *
 * Quem chega aqui NÃO tem conta — o token da URL é a credencial inteira. Cria
 * a conta já DENTRO do ambiente do convite (não um INDIVIDUAL novo) e o backend
 * devolve a sessão pronta.
 */
export function AceitarConvitePage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { recarregar } = useAuth();

  const conviteQuery = useConvitePublicoControllerDados(token, {
    query: { retry: false },
  });
  const aceitar = useConvitePublicoControllerAceitar();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  if (conviteQuery.isPending) {
    return (
      <AuthShell descricao="Convite">
        <div className="flex items-center justify-center gap-3 py-8 text-sm text-brand">
          <Loader2 className="h-4 w-4 animate-spin" />
          Carregando o convite…
        </div>
      </AuthShell>
    );
  }

  if (conviteQuery.isError || !conviteQuery.data) {
    const erro = conviteQuery.error;
    const mensagem =
      erro instanceof ApiError && erro.codigo === 'CONVITE_EXPIRADO'
        ? 'Este convite expirou. Peça um novo ao administrador do ambiente.'
        : erro instanceof ApiError && erro.codigo === 'CONVITE_INDISPONIVEL'
          ? 'Este convite já foi usado ou revogado.'
          : 'Convite não encontrado. Confira o link.';
    return (
      <AuthShell descricao="Convite indisponível">
        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertDescription className="text-sm">{mensagem}</AlertDescription>
          </Alert>
          <Button asChild variant="outline" className="w-full">
            <a href={ROTAS.login}>Ir para o login</a>
          </Button>
        </div>
      </AuthShell>
    );
  }

  const convite = conviteQuery.data;

  const aoEnviar = handleSubmit(async (dados) => {
    // Convite para o cargo de Médico: CRMV + UF viram obrigatórios (o backend
    // também recusa com 422, isto só antecipa o erro no campo certo).
    if (convite.exigeCrmv) {
      let faltou = false;
      if (!dados.crmv?.trim()) {
        setError('crmv', { message: 'Informe o CRMV' });
        faltou = true;
      }
      if (!dados.ufCrmv) {
        setError('ufCrmv', { message: 'Selecione a UF' });
        faltou = true;
      }
      if (faltou) return;
    }

    try {
      await aceitar.mutateAsync({
        token,
        data: {
          nomeCompleto: dados.nomeCompleto,
          senha: dados.senha,
          ...(dados.telefone ? { telefone: dados.telefone } : {}),
          ...(dados.crmv ? { crmv: dados.crmv } : {}),
          ...(dados.ufCrmv ? { ufCrmv: dados.ufCrmv } : {}),
        },
      });
      await recarregar();
      navigate(ROTAS.dashboard, { replace: true });
    } catch (erro) {
      setError('root', {
        message:
          erro instanceof ApiError
            ? erro.message
            : 'Não foi possível concluir. Tente de novo.',
      });
    }
  });

  return (
    <AuthShell descricao="Você foi convidado" larguraMaxima="max-w-lg">
      <form onSubmit={aoEnviar} className="space-y-4" noValidate>
        <Alert className="bg-brand/10 border-brand/20">
          <AlertDescription className="text-sm">
            <strong>{convite.convidadoPor}</strong> convidou você para{' '}
            <strong>{convite.ambienteNome}</strong> como <strong>{convite.cargoNome}</strong>.
            {convite.exigeCrmv && ' Como o cargo é de Médico, o CRMV e a UF são obrigatórios.'}
          </AlertDescription>
        </Alert>

        {errors.root?.message && (
          <Alert variant="destructive" className="bg-red-100 border-red-300 text-red-800">
            <AlertDescription>{errors.root.message}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label className="text-brand font-medium">E-mail</Label>
          <Input value={convite.email} disabled readOnly />
        </div>

        <Campo id="nomeCompleto" rotulo="Nome completo" erro={errors.nomeCompleto?.message}>
          <Input id="nomeCompleto" placeholder="Seu nome" {...register('nomeCompleto')} />
        </Campo>

        <Campo id="senha" rotulo="Senha" erro={errors.senha?.message}>
          <InputSenha
            id="senha"
            placeholder="Mínimo 8 caracteres"
            autoComplete="new-password"
            {...register('senha')}
          />
        </Campo>

        <Campo id="confirmarSenha" rotulo="Confirmar senha" erro={errors.confirmarSenha?.message}>
          <InputSenha
            id="confirmarSenha"
            placeholder="Repita a senha"
            autoComplete="new-password"
            {...register('confirmarSenha')}
          />
        </Campo>

        <Campo id="telefone" rotulo="Telefone (opcional)" erro={errors.telefone?.message}>
          <Input id="telefone" placeholder="(00) 00000-0000" {...register('telefone')} />
        </Campo>

        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <Campo
              id="crmv"
              rotulo={convite.exigeCrmv ? 'CRMV' : 'CRMV (opcional)'}
              erro={errors.crmv?.message}
            >
              <Input id="crmv" placeholder="Número do CRMV" {...register('crmv')} />
            </Campo>
          </div>
          <Campo id="ufCrmv" rotulo="UF" erro={errors.ufCrmv?.message}>
            <Select
              value={watch('ufCrmv')}
              onValueChange={(v) => setValue('ufCrmv', v as Formulario['ufCrmv'])}
            >
              <SelectTrigger id="ufCrmv">
                <SelectValue placeholder="UF" />
              </SelectTrigger>
              <SelectContent>
                {UFS.map((uf) => (
                  <SelectItem key={uf} value={uf}>
                    {uf}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-brand hover:bg-brand/80 text-white shadow-lg hover:shadow-xl transition-all"
        >
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Criar conta e entrar
        </Button>
      </form>
    </AuthShell>
  );
}

function Campo({
  id,
  rotulo,
  erro,
  children,
}: {
  id: string;
  rotulo: string;
  erro?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-brand font-medium">
        {rotulo}
      </Label>
      {children}
      {erro && <p className="text-xs text-destructive">{erro}</p>}
    </div>
  );
}
